import type { ColumnDef } from '@tanstack/react-table'
import { OrderAside } from '@/components/orders/OrderAside'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Typography } from '@/components/ui/typography'
import type {
  SalesTaxReportJurisdiction,
  SalesTaxReportJurisdictionDetail,
} from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import { formatRateSummary } from './-presentation'

const salesTaxJurisdictionDetailColumns: ColumnDef<SalesTaxReportJurisdictionDetail>[] = [
  {
    accessorKey: 'label',
    header: 'District',
    enableSorting: false,
    cell: ({ row }) => <span className="font-medium">{row.original.label}</span>,
    meta: { kind: 'primary', label: 'District' },
  },
  {
    accessorKey: 'gross_taxable_sales',
    header: 'Taxable Amount',
    enableSorting: false,
    cell: ({ row }) => formatCurrency(row.original.gross_taxable_sales),
    meta: { kind: 'money', label: 'Taxable Amount', align: 'end' },
  },
  {
    accessorKey: 'untaxable_amount',
    header: 'Non-taxable Amount',
    enableSorting: false,
    cell: ({ row }) => formatCurrency(row.original.untaxable_amount),
    meta: { kind: 'money', label: 'Non-taxable Amount', align: 'end' },
  },
  {
    accessorKey: 'tax_rates',
    header: 'Tax Rate',
    enableSorting: false,
    cell: ({ row }) => formatRateSummary(row.original.tax_rates),
    meta: { kind: 'number', label: 'Tax Rate', align: 'end' },
  },
  {
    accessorKey: 'tax_collected',
    header: 'Tax Collected',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="font-medium">{formatCurrency(row.original.tax_collected)}</span>
    ),
    meta: { kind: 'money', label: 'Tax Collected', align: 'end' },
  },
]

type SalesTaxJurisdictionAsideProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  jurisdiction: SalesTaxReportJurisdiction | null
}

export function SalesTaxJurisdictionAside({
  open,
  onOpenChange,
  jurisdiction,
}: SalesTaxJurisdictionAsideProps) {
  return (
    <OrderAside open={open} onOpenChange={onOpenChange}>
      {jurisdiction ? <SalesTaxJurisdictionAsideContent jurisdiction={jurisdiction} /> : null}
    </OrderAside>
  )
}

function SalesTaxJurisdictionAsideContent({
  jurisdiction,
}: {
  jurisdiction: SalesTaxReportJurisdiction
}) {
  return (
    <div className="space-y-5 px-5 py-5 sm:px-6 sm:py-6">
      <div className="min-w-0 pr-12">
        <Typography variant="h3" className="pb-0">
          {jurisdiction.jurisdiction_label}
        </Typography>
        <Typography variant="supporting" className="mt-2 block">
          Special district breakdown for this jurisdiction
        </Typography>
      </div>

      <div className="border-t border-border/60" />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <AsideMetric
          label="Taxable Amount"
          value={formatCurrency(jurisdiction.gross_taxable_sales)}
        />
        <AsideMetric
          label="Non-taxable Amount"
          value={formatCurrency(jurisdiction.untaxable_amount)}
        />
        <AsideMetric label="Tax Rate" value={formatRateSummary(jurisdiction.tax_rates)} />
        <AsideMetric label="Tax Collected" value={formatCurrency(jurisdiction.tax_collected)} />
      </div>

      <IndexDataTable
        mode="client"
        data={jurisdiction.details}
        columns={salesTaxJurisdictionDetailColumns}
        getRowId={(detail) => detail.label}
        pagination={false}
      />
    </div>
  )
}

function AsideMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border/60 bg-background/20 px-3 py-3">
      <Typography variant="eyebrow">{label}</Typography>
      <div className="mt-2 text-sm font-medium tabular-nums text-foreground">{value}</div>
    </div>
  )
}
